/* eslint-disable react/no-unescaped-entities */
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import Iconify from '../Iconify';

const SERIF_STACK = "'DM Serif Display', 'Playfair Display', 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const STAR_GOLD = '#E8A33D';
const SCENE_MS = 4500;

const formatStarRange = (threshold) => {
    // Threshold is the inclusive lower bound that routes to Google.
    // "4 or 5", "5 only", etc.
    if (threshold >= 5) return '5 stars';
    if (threshold === 4) return '4 or 5 stars';
    if (threshold <= 1) return 'any star rating';
    return `${threshold} stars or higher`;
};

const formatBelowRange = (threshold) => {
    // The "stays internal" copy.
    if (threshold >= 5) return '4 stars or below';
    if (threshold === 4) return '3 stars or below';
    if (threshold <= 1) return '(no ratings)';
    return `below ${threshold} stars`;
};

const formatHighBucket = (threshold) => {
    // Pill copy on split-outcome card.
    if (threshold >= 5) return '5★ patients';
    return `${threshold}+★ patients`;
};

const formatLowBucket = (threshold) => {
    if (threshold >= 5) return '1–4★ patients';
    if (threshold === 4) return '1–3★ patients';
    if (threshold === 3) return '1–2★ patients';
    if (threshold === 2) return '1★ patients';
    return '(no patients)';
};

const buildScenes = (threshold) => [
    {
        title: 'Generate the link',
        body: 'In the CRM, search for the patient and click Generate. A unique link tied to their last appointment is created in one tap.',
        device: 'crm',
    },
    {
        title: 'They get it in seconds',
        body: 'The link is delivered via WhatsApp, SMS or email — whichever channel you have configured. Patients receive it on their phone within seconds.',
        device: 'phone',
        phoneScene: 'whatsapp',
    },
    {
        title: 'They tap to open',
        body: 'A mobile-friendly rating page loads with your practice branding. No login. No app to download. Five stars, one comment box — done in 10 seconds.',
        device: 'phone',
        phoneScene: 'rating',
    },
    {
        title: 'They rate their visit',
        body: 'Most patients give 4 or 5 stars. A few give less. Either way, you hear from them before they think about leaving a public review.',
        device: 'phone',
        phoneScene: 'rated',
    },
    {
        title: 'Two paths, one decision',
        body: `${formatStarRange(threshold)}? The patient is auto-routed to your Google review page while they're happy. ${formatBelowRange(threshold)}? The feedback stays here, privately, so you can call them and fix it.`,
        device: 'split',
    },
];

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 167, 111, 0.4); }
    50% { transform: scale(1.04); box-shadow: 0 0 0 10px rgba(0, 167, 111, 0); }
`;

const typeIn = keyframes`
    from { width: 0; }
    to { width: 100%; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

export default function PatientFeedbackWalkthrough({ open, onClose, threshold = 4 }) {
    const theme = useTheme();
    const [scene, setScene] = useState(0);
    const [playing, setPlaying] = useState(true);
    const intervalRef = useRef(null);

    const SCENES = useMemo(() => buildScenes(threshold), [threshold]);

    useEffect(() => {
        if (!open) {
            setScene(0);
            setPlaying(true);
        }
    }, [open]);

    const sceneCount = SCENES.length;
    useEffect(() => {
        if (!open || !playing) {
            clearInterval(intervalRef.current);
            return undefined;
        }
        intervalRef.current = setInterval(() => {
            setScene((s) => (s + 1) % sceneCount);
        }, SCENE_MS);
        return () => clearInterval(intervalRef.current);
    }, [open, playing, sceneCount]);

    const current = SCENES[scene];

    const handleJump = (idx) => {
        setScene(idx);
        // restart the interval timing from this scene
        clearInterval(intervalRef.current);
        if (playing) {
            intervalRef.current = setInterval(() => {
                setScene((s) => (s + 1) % sceneCount);
            }, SCENE_MS);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: '#0F1620',
                    backgroundImage: `radial-gradient(circle at 20% 0%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 50%), radial-gradient(circle at 80% 100%, ${alpha(STAR_GOLD, 0.1)} 0%, transparent 50%)`,
                },
            }}
        >
            {/* Top bar */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        <Iconify icon="solar:chat-square-like-bold-duotone" width={18} sx={{ color: theme.palette.primary.light }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                            How Patient Feedback works
                        </Typography>
                        <Typography sx={{ fontFamily: SERIF_STACK, fontSize: 18, color: '#fff', lineHeight: 1.2 }}>
                            From <Box component="span" sx={{ fontStyle: 'italic', color: theme.palette.primary.light }}>chair</Box> to Google in five steps
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton size="small" onClick={() => setPlaying((p) => !p)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <Iconify icon={playing ? 'solar:pause-bold' : 'solar:play-bold'} width={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setScene(0); setPlaying(true); }} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <Iconify icon="solar:restart-bold" width={18} />
                    </IconButton>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
                        <Iconify icon="eva:close-fill" width={20} />
                    </IconButton>
                </Stack>
            </Stack>

            {/* Progress dots */}
            <Stack direction="row" spacing={0.75} sx={{ px: 3, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {SCENES.map((s, idx) => {
                    const active = idx === scene;
                    const past = idx < scene;
                    return (
                        <Box
                            key={s.title}
                            onClick={() => handleJump(idx)}
                            sx={{
                                flex: 1,
                                height: 3,
                                borderRadius: '999px',
                                bgcolor: 'rgba(255,255,255,0.08)',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: past ? theme.palette.primary.main : 'transparent',
                                    transition: 'background 0.3s',
                                },
                            }}
                        >
                            {active && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        bgcolor: theme.palette.primary.main,
                                        transformOrigin: 'left',
                                        animation: playing ? `${typeIn} ${SCENE_MS}ms linear` : 'none',
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {/* Body: copy + device */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' },
                    minHeight: 540,
                }}
            >
                {/* Copy panel */}
                <Box sx={{ p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography
                        sx={{
                            fontFamily: SERIF_STACK,
                            fontSize: 18,
                            color: STAR_GOLD,
                            mb: 1.5,
                            letterSpacing: '0.5px',
                        }}
                    >
                        Step {String(scene + 1).padStart(2, '0')} / {String(SCENES.length).padStart(2, '0')}
                    </Typography>

                    <Box key={`title-${scene}`} sx={{ animation: `${fadeIn} 0.45s ease` }}>
                        <Typography
                            component="h2"
                            sx={{
                                fontFamily: SERIF_STACK,
                                fontSize: { xs: 32, md: 40 },
                                lineHeight: 1.1,
                                color: '#fff',
                                fontWeight: 400,
                                letterSpacing: '-0.5px',
                                mb: 2.5,
                            }}
                        >
                            {current.title}
                        </Typography>
                        <Typography sx={{ fontSize: 16, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', maxWidth: 460 }}>
                            {current.body}
                        </Typography>
                    </Box>
                </Box>

                {/* Device panel */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(180deg, ${alpha('#000', 0.0)} 0%, ${alpha('#000', 0.3)} 100%)`,
                        overflow: 'hidden',
                        py: 4,
                    }}
                >
                    <Box key={`device-${scene}`} sx={{ animation: `${fadeIn} 0.5s ease` }}>
                        {current.device === 'crm' && <CRMMock theme={theme} />}
                        {current.device === 'phone' && <PhoneMock theme={theme} scene={current.phoneScene} />}
                        {current.device === 'split' && <SplitOutcome theme={theme} threshold={threshold} />}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}

PatientFeedbackWalkthrough.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    threshold: PropTypes.number,
};

// ─────────────────────────────────────────────────────────────────────────
// CRM Mock — Step 1
// ─────────────────────────────────────────────────────────────────────────

function CRMMock({ theme }) {
    return (
        <Box
            sx={{
                width: 460,
                bgcolor: '#fff',
                borderRadius: 2.5,
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
                overflow: 'hidden',
            }}
        >
            {/* Browser chrome */}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ p: 1.5, bgcolor: '#F1F5F9' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF5F57' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FEBC2E' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28C840' }} />
            </Stack>

            <Box sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1B2330', mb: 2 }}>
                    Generate Feedback Link
                </Typography>

                {/* Search field with auto-typing animation */}
                <Box
                    sx={{
                        position: 'relative',
                        p: 1.5,
                        border: '1px solid #E2E8F0',
                        borderRadius: 1.5,
                        mb: 1.5,
                        bgcolor: '#FAFBFC',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="uil:search" width={16} sx={{ color: 'text.disabled' }} />
                        <Box
                            sx={{
                                fontSize: 13,
                                color: '#1B2330',
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                animation: `${typeIn} 1.2s steps(15, end)`,
                                animationFillMode: 'both',
                            }}
                        >
                            Emma Marshall
                        </Box>
                    </Stack>
                </Box>

                {/* Result row */}
                <Box
                    sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 1.5,
                        mb: 2,
                        animation: `${fadeIn} 0.5s 1s both`,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: '#E8F5E9',
                                color: '#1B5E20',
                                display: 'grid',
                                placeItems: 'center',
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        >
                            EM
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1B2330' }}>
                                Emma Marshall
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                Invisalign consultation · 2 days ago
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: '999px',
                                fontSize: 10,
                                fontWeight: 600,
                                bgcolor: alpha('#1B8E3F', 0.12),
                                color: '#1B8E3F',
                            }}
                        >
                            ATTENDED
                        </Box>
                    </Stack>
                </Box>

                {/* Generate button with pulse */}
                <Box
                    sx={{
                        p: 1.25,
                        bgcolor: theme.palette.primary.main,
                        color: '#fff',
                        borderRadius: 1.5,
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: 13,
                        animation: `${pulse} 1.8s 1.8s ease-in-out infinite`,
                    }}
                >
                    Generate feedback link
                </Box>

                {/* Generated URL appears */}
                <Box
                    sx={{
                        mt: 1.5,
                        p: 1,
                        bgcolor: '#F8FAFC',
                        border: '1px dashed #CBD5E1',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: 'text.secondary',
                        animation: `${fadeIn} 0.5s 2.6s both`,
                        opacity: 0,
                        animationFillMode: 'forwards',
                    }}
                >
                    https://dental-sem.co.uk/r/aj8K4q…
                </Box>
            </Box>
        </Box>
    );
}

CRMMock.propTypes = { theme: PropTypes.object.isRequired };

// ─────────────────────────────────────────────────────────────────────────
// Phone Mock — Steps 2, 3, 4
// ─────────────────────────────────────────────────────────────────────────

function PhoneMock({ theme, scene }) {
    return (
        <Box
            sx={{
                width: 280,
                height: 560,
                bgcolor: '#000',
                borderRadius: '40px',
                p: '14px',
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
                position: 'relative',
            }}
        >
            {/* Notch */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 90,
                    height: 22,
                    bgcolor: '#000',
                    borderRadius: '0 0 14px 14px',
                    zIndex: 2,
                }}
            />

            {/* Screen */}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: scene === 'whatsapp' ? '#0B141A' : '#fff',
                    borderRadius: '28px',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                {scene === 'whatsapp' && <WhatsAppScene theme={theme} />}
                {scene === 'rating' && <RatingScene theme={theme} />}
                {scene === 'rated' && <RatedScene theme={theme} />}
            </Box>
        </Box>
    );
}

PhoneMock.propTypes = { theme: PropTypes.object.isRequired, scene: PropTypes.string.isRequired };

function WhatsAppScene() {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* WA header */}
            <Box sx={{ p: 2, pt: 4, bgcolor: '#1F2C34', color: '#fff' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#00A884', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                        SH
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Smile Health</Typography>
                        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>online</Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Message */}
            <Box sx={{ flex: 1, p: 1.5, bgcolor: '#0B141A', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <Box
                    sx={{
                        alignSelf: 'flex-start',
                        maxWidth: '85%',
                        bgcolor: '#1F2C34',
                        color: '#fff',
                        p: 1.25,
                        borderRadius: '8px',
                        borderTopLeftRadius: 0,
                        animation: `${slideUp} 0.6s 0.5s ease both`,
                    }}
                >
                    <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>
                        Hi Emma! Thanks for visiting us today. We&apos;d love your quick feedback — it takes 10 seconds:
                    </Typography>
                    <Box
                        sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: 'rgba(0,168,132,0.15)',
                            borderRadius: 1,
                            border: '1px solid rgba(0,168,132,0.3)',
                        }}
                    >
                        <Typography sx={{ fontSize: 10, color: '#00A884', fontWeight: 600 }}>
                            🔗 smile-health.co.uk/feedback/aj8K4q
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', mt: 0.5, textAlign: 'right' }}>
                        12:42 ✓✓
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function RatingScene({ theme }) {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pt: 4 }}>
            {/* Header banner */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: '#fff',
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', opacity: 0.8 }}>
                    SMILE HEALTH DENTAL
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                    How was your visit, Emma?
                </Typography>
            </Box>

            <Box sx={{ p: 2.5, flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 2 }}>
                    Tap to rate your experience
                </Typography>

                {/* Empty stars row — pulsing */}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Iconify
                            key={i}
                            icon="solar:star-linear"
                            width={32}
                            sx={{
                                color: alpha('#000', 0.2),
                                animation: `${pulse} 1.8s ${i * 0.1}s ease-in-out infinite`,
                            }}
                        />
                    ))}
                </Stack>

                <Box
                    sx={{
                        mt: 3,
                        p: 1.25,
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: 1.25,
                        color: 'text.disabled',
                        fontSize: 11,
                        fontStyle: 'italic',
                    }}
                >
                    Leave a comment (optional)…
                </Box>
            </Box>
        </Box>
    );
}

RatingScene.propTypes = { theme: PropTypes.object.isRequired };

function RatedScene({ theme }) {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pt: 4 }}>
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: '#fff',
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', opacity: 0.8 }}>
                    SMILE HEALTH DENTAL
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                    How was your visit, Emma?
                </Typography>
            </Box>

            <Box sx={{ p: 2.5, flex: 1 }}>
                {/* Filled stars row — bouncing in */}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Iconify
                            key={i}
                            icon="solar:star-bold"
                            width={32}
                            sx={{
                                color: STAR_GOLD,
                                animation: `${fadeIn} 0.4s ${i * 0.12}s both`,
                                opacity: 0,
                            }}
                        />
                    ))}
                </Stack>

                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 1.25,
                        animation: `${fadeIn} 0.5s 0.7s both`,
                        opacity: 0,
                        animationFillMode: 'forwards',
                    }}
                >
                    <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: 'text.primary', lineHeight: 1.5 }}>
                        "Sarah was wonderful. Pain-free, gentle, took the time to explain everything."
                    </Typography>
                </Box>

                {/* Submit button */}
                <Box
                    sx={{
                        mt: 3,
                        py: 1,
                        bgcolor: theme.palette.primary.main,
                        color: '#fff',
                        borderRadius: '999px',
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        animation: `${fadeIn} 0.5s 1.5s both`,
                        opacity: 0,
                        animationFillMode: 'forwards',
                    }}
                >
                    Submit feedback
                </Box>

                {/* Success state */}
                <Box
                    sx={{
                        mt: 3,
                        p: 1.5,
                        bgcolor: alpha('#1B8E3F', 0.08),
                        border: '1px solid',
                        borderColor: alpha('#1B8E3F', 0.3),
                        borderRadius: 1.25,
                        textAlign: 'center',
                        animation: `${fadeIn} 0.5s 2.2s both`,
                        opacity: 0,
                        animationFillMode: 'forwards',
                    }}
                >
                    <Iconify icon="solar:check-circle-bold" width={20} sx={{ color: '#1B8E3F', mb: 0.5 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1B8E3F' }}>
                        Thanks for your feedback!
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

RatedScene.propTypes = { theme: PropTypes.object.isRequired };

// ─────────────────────────────────────────────────────────────────────────
// Split outcome — Step 5
// ─────────────────────────────────────────────────────────────────────────

function SplitOutcome({ theme, threshold }) {
    // Top card: stars at exactly the threshold (e.g. threshold=4 → 4 filled stars)
    const filledStarCount = Math.min(5, Math.max(1, threshold));
    // Bottom card: stars in the "stays internal" bucket. When threshold=1
    // there are no patients below it, so render 0 filled stars (not 1).
    const lowSampleCount = Math.max(0, Math.min(4, threshold - 1));
    return (
        <Stack direction="row" spacing={2.5} alignItems="flex-start">
            {/* Happy path — Google */}
            <Box
                sx={{
                    width: 220,
                    bgcolor: '#fff',
                    borderRadius: 2.5,
                    boxShadow: '0 20px 40px -16px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    animation: `${fadeIn} 0.6s ease both`,
                }}
            >
                <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box component="svg" viewBox="0 0 192 192" sx={{ width: 14, height: 14 }}>
                            <rect fill="#4285f4" height="58" rx="29" width="58" x="67" y="15" />
                            <rect fill="#34a853" height="58" rx="29" width="58" x="119" y="67" />
                            <rect fill="#fbbc04" height="58" rx="29" width="58" x="67" y="119" />
                            <rect fill="#ea4335" height="58" rx="29" width="58" x="15" y="67" />
                        </Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Google review</Typography>
                    </Stack>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#1B8E3F', textTransform: 'uppercase', mb: 1 }}>
                        {formatHighBucket(threshold)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1B2330', mb: 1.5 }}>
                        Auto-routed to your Google page
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Iconify key={i} icon={i <= filledStarCount ? 'solar:star-bold' : 'solar:star-linear'} width={14} sx={{ color: i <= filledStarCount ? STAR_GOLD : alpha('#000', 0.15) }} />
                        ))}
                    </Stack>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                        Public reviews. Better ranking. More patients.
                    </Typography>
                </Box>
            </Box>

            {/* Sad path — Internal */}
            <Box
                sx={{
                    width: 220,
                    bgcolor: '#fff',
                    borderRadius: 2.5,
                    boxShadow: '0 20px 40px -16px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    animation: `${fadeIn} 0.6s 0.3s ease both`,
                    opacity: 0,
                    animationFillMode: 'forwards',
                }}
            >
                <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="solar:shield-warning-bold-duotone" width={14} sx={{ color: theme.palette.warning.main }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Patient Feedback (private)</Typography>
                    </Stack>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#D14343', textTransform: 'uppercase', mb: 1 }}>
                        {formatLowBucket(threshold)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1B2330', mb: 1.5 }}>
                        Surfaces here — never goes public
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Iconify key={i} icon={i <= lowSampleCount ? 'solar:star-bold' : 'solar:star-linear'} width={14} sx={{ color: i <= lowSampleCount ? STAR_GOLD : alpha('#000', 0.15) }} />
                        ))}
                    </Stack>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                        Call them. Fix it. Win them back.
                    </Typography>
                </Box>
            </Box>
        </Stack>
    );
}

SplitOutcome.propTypes = { theme: PropTypes.object.isRequired, threshold: PropTypes.number.isRequired };
